/**
 * Plantillas de rol (recetarios de UI). No son entidades de API: al guardar
 * se crea un rol custom con estas keys. Las claves tienen que existir en el
 * catálogo (`ALL_PERMISSION_KEYS`).
 */
import { ALL_PERMISSION_KEYS } from './catalog';

/** Tope de roles personalizados por dueño (sistema no cuenta). UI y API lo enforcean. */
export const MAX_CUSTOM_ROLES = 8;

export type RoleTemplateId = 'readOnly' | 'financeOnly' | 'clinicOps';

export interface RoleTemplate {
  id: RoleTemplateId;
  nameKey: string;
  savedNameKey: string;
  descriptionKey: string;
  permissionKeys: string[];
}

const ALL_METRICS = ALL_PERMISSION_KEYS.filter((key) => key.startsWith('metrics.'));

const TEAM_KEYS = new Set(['assistants.view', 'assistants.manage', 'permissions.manage']);

export const ROLE_TEMPLATES: RoleTemplate[] = [
  {
    id: 'readOnly',
    nameKey: 'permissions.templates.items.readOnly.name',
    savedNameKey: 'permissions.templates.items.readOnly.savedName',
    descriptionKey: 'permissions.templates.items.readOnly.description',
    permissionKeys: [
      'dashboard.view',
      ...ALL_METRICS,
      'reports.view',
      'communications.view',
    ],
  },
  {
    id: 'financeOnly',
    nameKey: 'permissions.templates.items.financeOnly.name',
    savedNameKey: 'permissions.templates.items.financeOnly.savedName',
    descriptionKey: 'permissions.templates.items.financeOnly.description',
    permissionKeys: [
      'metrics.caja',
      'metrics.tendencia',
      'metrics.ranking_planes',
      'metrics.plan_billing',
      'metrics.plan_recaudacion',
      'reports.view',
    ],
  },
  {
    id: 'clinicOps',
    nameKey: 'permissions.templates.items.clinicOps.name',
    savedNameKey: 'permissions.templates.items.clinicOps.savedName',
    descriptionKey: 'permissions.templates.items.clinicOps.description',
    permissionKeys: ALL_PERMISSION_KEYS.filter((key) => !TEAM_KEYS.has(key)),
  },
];

export function templateById(id: string): RoleTemplate | undefined {
  return ROLE_TEMPLATES.find((template) => template.id === id);
}

export function samePermissionKeys(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  const other = new Set(b);
  return a.every((key) => other.has(key));
}

/** Reusa un rol ya guardado (sistema o custom) con el mismo set de permisos. */
export function findRoleMatchingTemplate<T extends { permissionKeys: string[] }>(
  roles: T[],
  template: RoleTemplate
): T | undefined {
  return roles.find((role) => samePermissionKeys(role.permissionKeys, template.permissionKeys));
}

export function nextUniqueRoleName(base: string, existingNames: string[]): string {
  const taken = new Set(existingNames.map((name) => name.trim().toLowerCase()));
  const trimmed = base.trim();
  if (!taken.has(trimmed.toLowerCase())) return trimmed;
  for (let n = 2; n < 50; n++) {
    const candidate = `${trimmed} ${n}`;
    if (!taken.has(candidate.toLowerCase())) return candidate;
  }
  return `${trimmed} ${Date.now()}`;
}
